import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  input,
  Output,
  output,
  viewChild,
  ViewChild,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldConfig, SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldMultiselectModule, SeFeMultiselectOptions} from 'se-fe-form-field-multiselect';
import {SeFeFormFieldTextComponent, SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeModalButton, SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {trimFormValue} from '../../../../utils/form-utils';
import {
  ProductOrganizationUpdate,
  UpdateProductDetail,
} from '../../../../features/product/components/product-detail/product-detail.store';
import {CatalogSummary} from '../../../../models/clare';

@Component({
  selector: 'item-org-modal',
  imports: [
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeFormFieldModule,
    SeFeFormFieldMultiselectModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeIconModule,
    SeFeModalModule,
    SeFeSpinnerModule,
  ],
  templateUrl: './item-org-modal.component.html',
  styleUrls: ['./item-org-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemOrgModalComponent {
  @Output() updateBrand = new EventEmitter<UpdateProductDetail<ProductOrganizationUpdate>>();
  @ViewChild('orgModal') orgModal: SeFeModalComponent;
  vendorNameInput = viewChild.required<SeFeFormFieldTextComponent, ElementRef<HTMLElement>>('vendorName', {
    read: ElementRef,
  });

  error = input<string>();
  vendor = input<{brand: string; name: string; taxCode: string}>();
  catalogs = input<CatalogSummary[]>([]);
  catalogsLoading = input(false);
  catalogsOnly = input(false);
  selectedCatalogIds = input<string[]>([]);
  catalogsRequested = output<void>();

  form = new FormGroup({
    brand: new FormControl('', [Validators.maxLength(100)]),
    vendorName: new FormControl('', [Validators.required]),
    taxCode: new FormControl('', [Validators.maxLength(25)]),
    catalogIds: new FormControl<string[]>([], {nonNullable: true}),
  });

  catalogOptions = computed<SeFeMultiselectOptions>(() => ({
    sections: [
      {
        options: this.catalogs().map(catalog => ({text: catalog.name, value: String(catalog.id)})),
      },
    ],
  }));

  productBrandConfig: SeFeFormFieldConfig = {
    label: 'Brand',
    errorMessages: {
      maxlength: 'Brand name exceeds the maximum length of 100 characters',
    },
  };
  productVendorConfig: SeFeFormFieldConfig = {
    label: 'Vendor',
  };
  productTaxCodeConfig: SeFeFormFieldConfig = {
    label: 'Tax code',
    errorMessages: {
      maxlength: 'Tax code exceeds maximum length of 25 characters',
    },
  };
  productCatalogsConfig: SeFeFormFieldConfig = {
    label: 'Catalogs',
  };

  orgModalButtons: SeFeModalButton[] = [
    {
      cancel: true,
      label: 'Cancel',
      action: () => {
        this.resetForm();
        this.orgModal.close();
        return 'Cancel.';
      },
      loadingLabel: 'Cancel',
    },
    {
      primary: true,
      label: 'Save',
      action: async () => {
        this.handleAction();
        return 'Saving';
      },
      loadingLabel: 'Updating Product',
    },
  ];

  constructor(private cdr: ChangeDetectorRef) {
    effect(() => {
      this.form.patchValue({
        brand: this.vendor()?.brand,
        vendorName: this.vendor()?.name,
        taxCode: this.vendor()?.taxCode,
        catalogIds: [...this.selectedCatalogIds()],
      });
    });
  }

  setVendorTabIndex(): void {
    this.vendorNameInput().nativeElement.querySelector('input')!.tabIndex = -1;
  }

  onBeforeOpen(): void {
    this.setVendorTabIndex();
    this.catalogsRequested.emit();
  }

  handleAction(): void {
    trimFormValue(this.form, 'brand');
    trimFormValue(this.form, 'taxCode');
    this.cdr.markForCheck();
    if (this.form.valid) {
      this.updateBrand.emit({
        data: {
          brand: this.form.get('brand')!.value!,
          taxCode: this.form.get('taxCode')!.value!,
          catalogIds: this.form.controls.catalogIds.value.map(catalogId => Number(catalogId)),
        },
        modal: this.orgModal,
      });
    } else {
      this.orgModal?.cancelSpinner?.();
    }
  }

  resetForm(): void {
    this.form.reset({
      brand: this.vendor()?.brand,
      vendorName: this.vendor()?.name,
      taxCode: this.vendor()?.taxCode,
      catalogIds: [...this.selectedCatalogIds()],
    });
    this.cdr.detectChanges();
  }
}
